import { FC, useEffect, useRef, useState } from 'react';
import cns from 'classnames';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { MapUiSize, MAPUI_SIZES } from '../../common/types';
import styled from '@emotion/styled';
import { space, SpaceProps } from 'styled-system';
import { useOnClickOutside } from '../../hooks';

const appRoot = document.getElementById('root');

export const MODAL_CLASSNAME = '__mapui_modal';

const applyMainStyles =
  ({ overlay }: Pick<ModalProps, 'overlay'>) =>
  (theme) => {
    const wrapper = css(theme.modal.wrapper);

    const styles = [wrapper];

    if (overlay) {
      styles.push(css(theme.modal.overlay));
    }

    return css(...styles);
  };

const sectionStyle =
  ({ size }: Pick<ModalProps, 'size'>) =>
  (theme) => {
    const boxStyle = css(theme.modal.content);
    const sizeStyle = css(theme.modal.size[size]);

    const styles = [boxStyle, sizeStyle];

    return css(...styles);
  };

export type ModalSize = MapUiSize | 'full';

export const MODAL_SIZES = { ...MAPUI_SIZES, FULL: 'full' };

export interface ModalProps extends SpaceProps {
  onOpen?: Function;
  onClose?: Function;
  className?: string;
  overlay?: boolean;
  size?: ModalSize;
  onClickOutside?: () => void;
}

const ModalContainer = styled.div<ModalProps>(
  ({ theme, overlay = true }) => ({
    ...applyMainStyles({ overlay })(theme),
  }),
  ({ className }) => ({
    className: cns(MODAL_CLASSNAME, className),
  }),
  space
);

const Modal: FC<ModalProps> = ({
  children,
  overlay = true,
  size = 'md',
  onClose,
  onOpen,
  onClickOutside = () => void 0,
  ...rest
}) => {
  const [internalElement] = useState(() => document.createElement('div'));
  const sectionRef = useRef();

  useOnClickOutside(sectionRef, onClickOutside);

  useEffect(() => {
    appRoot?.appendChild(internalElement);
    onOpen && onOpen();

    return () => {
      appRoot?.removeChild(internalElement);
      onClose && onClose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <ModalContainer overlay={overlay} {...rest}>
      <section ref={sectionRef} css={sectionStyle({ size })}>
        {children}
      </section>
    </ModalContainer>,
    internalElement
  );
};

export { Modal };
